import React, { useCallback, useState } from 'react';
import api from '../../../../config/configApi';
import { Button, Box, Breadcrumbs, Chip, emphasize, FormControl, FormLabel, InputAdornment, styled, TextField, Alert } from '@mui/material';
import { MdHome, MdVisibility } from 'react-icons/md';
import { isEmpty } from 'lodash';
import toast from 'react-hot-toast';
import { useLocation, useNavigate } from 'react-router-dom';
import { logError } from '../../../../services/logService';
import { useAuth } from '../../../../context/AuthContext';

const StyledBreadcrumb = styled(Chip)(({ theme }) => {
    return {
        backgroundColor: theme.palette.grey[100],
        height: theme.spacing(3),
        color: (theme.vars || theme).palette.text.primary,
        fontWeight: theme.typography.fontWeightRegular,
        '&:hover, &:focus': {
            backgroundColor: emphasize(theme.palette.grey[100], 0.06),
            ...theme.applyStyles('dark', {
                backgroundColor: emphasize(theme.palette.grey[800], 0.06),
            }),
        },
        '&:active': {
            boxShadow: theme.shadows[1],
            backgroundColor: emphasize(theme.palette.grey[100], 0.12),
            ...theme.applyStyles('dark', {
                backgroundColor: emphasize(theme.palette.grey[800], 0.12),
            }),
        },
        ...theme.applyStyles('dark', {
            backgroundColor: theme.palette.grey[800],
        }),
    };
});

function DashboardNovoUsuario() {
    const { user } = useAuth();
    const location = useLocation();
    const navigate = useNavigate();

    const [isButtonDisabled, setIsButtonDisabled] = useState(false);
    const [nomeFocused, setNomeFocused] = useState(false);
    const [emailFocused, setEmailFocused] = useState(false);
    const [passwordFocused, setPasswordFocused] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [userForm, setUserForm] = useState({
        nome: '',
        email: '',
        password: '',
    })
    const [formError, setFormError] = useState({
        nome: null,
        email: null,
        password: null
    });

    const handleChange = (e) => {
        setUserForm((prev) => ({
            ...prev,
            [e.target.name]: e.target.value
        }));
    }

    const handleClickShowPassword = () => {
        setShowPassword(!showPassword);
    }

    const validateForm = useCallback(() => {
        let currentErrors = {};
        let formIsValid = true;

        if (isEmpty(userForm.nome)) {
            currentErrors.nome = 'Preencha o nome';
        }
        if (isEmpty(userForm.email) || !/\S+@\S+\.\S+/.test(userForm.email)) {
            currentErrors.email = 'Preencha com um email valido';
            formIsValid = false;
        }
        if (isEmpty(userForm.password)) {
            currentErrors.password = 'Preencha a senha';
            formIsValid = false;
        }

        setFormError(currentErrors);
        return formIsValid;
    }, [userForm])

    const handleSubmit = async (e) => {
        e.preventDefault();

        const formIsValid = validateForm();
        if (!formIsValid) {
            toast.error('Por favor, corrija os erros no formulário')
            return;
        }

        setIsButtonDisabled(true);

        const toastId = toast.loading('Salvando usuário novo...');

        try {
            const response = await api.post('/api/auth/register', userForm);
            toast.success('Usuário criado com sucesso!', { id: toastId });
            setTimeout(() => navigate('/dashboard/usuarios'), 2000)
        } catch (err) {
            const errMessage = err.response?.data?.msg || err.message || 'Erro desconhecido ao criar usuário.';
            toast.error(`Erro inesperado ao criar usuário: ${errMessage}`);
            await logError({
                email: user?.email || 'N/A',
                log_erro: `Erro crítico inesperado ao criar Usuário: ${errMessage}`,
                formulario: location.pathname,
            });
        }
    }

    return (
        <>
            <div className='card shadow border-0 w-100 flex-row p-4 res-col'>
                <h5 className='mb-0'>Novo usuário</h5>
                <Breadcrumbs
                    aria-label='breadcrumb'
                    className='ms-auto breadcrumbs_'
                >
                    <StyledBreadcrumb label='Dashboard' icon={<MdHome fontSize='small' />} />
                    <StyledBreadcrumb label='Usuário' />
                    <StyledBreadcrumb label='Novo' />
                </Breadcrumbs>
            </div>
            {!user.criar && (
                <Alert severity='warning'>Seu usuário não possui permissão para criar!</Alert>
            )}
            {user.criar && (
                <Box
                    className='form'
                    component='form'
                    onSubmit={handleSubmit}
                    noValidate
                >
                    <div className='row'>
                        <div className='col-md-12'>
                            <div className='card p-4 mt-0'>
                                <h5 className='mb-4'>Informações</h5>

                                <div className='row'>
                                    <div className='col'>
                                        <FormControl
                                            className='mb-4'
                                            fullWidth
                                        >
                                            <FormLabel
                                                htmlFor='nome'
                                                sx={{ fontSize: '12px', fontWeight: 500, marginBottom: '5px', color: nomeFocused ? 'primary.main' : '' }}
                                                error={formError?.nome}
                                            >
                                                Nome
                                            </FormLabel>
                                            <TextField
                                                required
                                                id='nome'
                                                name='nome'
                                                type='text'
                                                variant='outlined'
                                                error={formError?.nome}
                                                helperText={formError?.nome}
                                                color={formError?.nome ? 'error' : 'primary'}
                                                onChange={handleChange}
                                                onFocus={() => setNomeFocused(!nomeFocused)}
                                                onBlur={() => setNomeFocused(!nomeFocused)}
                                            />
                                        </FormControl>
                                    </div>

                                    <div className='col'>
                                        <FormControl
                                            className='mb-4'
                                            fullWidth
                                        >
                                            <FormLabel
                                                htmlFor='email'
                                                sx={{ fontSize: '12px', fontWeight: 500, marginBottom: '5px', color: emailFocused ? 'primary.main' : '' }}
                                                error={formError?.email}
                                            >
                                                E-mail
                                            </FormLabel>
                                            <TextField
                                                required
                                                id='email'
                                                name='email'
                                                type='text'
                                                variant='outlined'
                                                error={formError?.email}
                                                helperText={formError?.email}
                                                color={formError?.email ? 'error' : 'primary'}
                                                onChange={handleChange}
                                                onFocus={() => setEmailFocused(!emailFocused)}
                                                onBlur={() => setEmailFocused(!emailFocused)}
                                            />
                                        </FormControl>
                                    </div>

                                    <div className='col'>
                                        <FormControl
                                            className='mb-4'
                                            fullWidth
                                        >
                                            <FormLabel
                                                htmlFor='password'
                                                sx={{ fontSize: '12px', fontWeight: 500, marginBottom: '5px', color: passwordFocused ? 'primary.main' : '' }}
                                                error={formError?.password}
                                            >
                                                Senha
                                            </FormLabel>
                                            <TextField
                                                required
                                                id='password'
                                                name='password'
                                                type={showPassword ? 'text' : 'password'}
                                                variant='outlined'
                                                error={formError?.password}
                                                helperText={formError?.password}
                                                color={formError?.password ? 'error' : 'primary'}
                                                onChange={handleChange}
                                                onFocus={() => setPasswordFocused(!passwordFocused)}
                                                onBlur={() => setPasswordFocused(!passwordFocused)}
                                                slotProps={{
                                                    input: {
                                                        endAdornment: (
                                                            <InputAdornment position='end'>
                                                                <MdVisibility onClick={handleClickShowPassword} style={{ cursor: 'pointer' }} />
                                                            </InputAdornment>
                                                        ),
                                                    },
                                                }}
                                            />
                                        </FormControl>
                                    </div>
                                </div>

                                <div className='d-flex justify-content-between'>
                                    <Button
                                        color='error'
                                        variant='contained'
                                        onClick={() => navigate('/dashboard')}
                                        disabled={isButtonDisabled}
                                    >
                                        Cancelar
                                    </Button>

                                    <Button
                                        type='submit'
                                        variant='contained'
                                        sx={{ color: '#fff' }}
                                        loading={isButtonDisabled}
                                    >
                                        Salvar
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </div>
                </Box>
            )}
        </>
    )
}

export default DashboardNovoUsuario;